import { Constants } from "appConstants";
import BaseForm from "components/BaseForm";
import ConfirmationModal from "components/ConfirmationModal";
import FileInput from "components/FileInput";
import Input from "components/Input";
import Loading from "components/Loading";
import { Select } from "components/Select";
import SwitchInput from "components/SwitchInput";
import { ErrorHelper } from "helpers";
import DateTimeHelper from "helpers/DateTimeHelper";
import FileHelper from "helpers/FileHelper";
import useStandardToast from "hooks/useStandardToast";
import { Term } from "models/term/Term";
import { TermType } from "models/term/TermType";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TenantService } from "services";
import { TermService } from "services/term";
import { TermTypeService } from "services/termType";
import * as Yup from "yup";
import "./styles.scss";

interface TermFormsInterface {
	type?: string;
	typeId?: string;
	fileName: string;
	activateNow: boolean;
	startDate: string;
}

const emptyValues: TermFormsInterface = {
	type: "",
	typeId: "",
	fileName: "",
	activateNow: false,
	startDate: ""
};

const MAX_DOCUMENT_SIZE = 5 * 1024 * 1024; // 5 MB

export default function TermForm() {
	const { activeTermId, termId } = useParams();

	const { showErrorToast, showSuccessToast } = useStandardToast();

	const navigate = useNavigate();

	const [activeTerm, setActiveTerm] = useState<Term | null>(null);
	const [termTypes, setTermTypes] = useState<TermType[]>([]);

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [isEditForm, setIsEditForm] = useState(false);
	const [loadSelect, setLoadSelect] = useState(false);
	const [showModal, setShowModal] = useState(false);

	const [title, setTitle] = useState<string>(
		Constants.strings.pages.term.addTerm
	);

	const [initialValues, setInitialValues] =
		useState<TermFormsInterface>(emptyValues);

	const [uniqueOption, setUniqueOption] = useState(false);

	const tenantService = useMemo(() => new TenantService(), []);
	const termTypeService = useMemo(() => new TermTypeService(), []);
	const termService = useMemo(() => new TermService(), []);

	const hasDateChanged = (date: string) => date !== initialValues.startDate;

	const validationSchema = useMemo(
		() =>
			Yup.object().shape({
				typeId: Yup.string().required(Constants.strings.inputs.requiredField),
				fileName: Yup.string().required(Constants.strings.inputs.requiredField),
				startDate: Yup.string()
					.required(Constants.strings.inputs.requiredField)
					.test(
						"is-greater-equal-to-today",
						Constants.strings.errors.startDateError,
						(date) => !DateTimeHelper.dateBeforeToday(date)
					)
					.test(
						"is-less-than-2100-12-31",
						Constants.strings.errors.maxDateError,
						(date) => DateTimeHelper.dateBefore(date, "2100-12-31")
					)
			}),
		[]
	);

	const onSubmit = useCallback(
		(data: any) => {
			setIsSubmitting(true);

			if (termId) {
				if (hasDateChanged(data.startDate)) {
					termService
						.updateDate(termId, data)
						.then(() => {
							navigate(-1);
							showSuccessToast(Constants.strings.success.termUpdatedTitle);
						})
						.catch((error) => {
							showErrorToast(ErrorHelper.getErrorMessage(error));
						})
						.finally(() => {
							setIsSubmitting(false);
						});
				} else {
					navigate(-1);
					showSuccessToast(Constants.strings.success.termUpdatedTitle);
				}
			} else {
				termService
					.createTerm(data)
					.then(() => {
						navigate(-1);
						showSuccessToast(Constants.strings.success.termAddedTitle);
					})
					.catch((error) => {
						showErrorToast(ErrorHelper.getErrorMessage(error));
					})
					.finally(() => {
						setIsSubmitting(false);
					});
			}
		},
		[tenantService]
	);

	const getTermTypes = useCallback(() => {
		setIsLoading(true);
		termTypeService
			.getAvailableTermTypes()
			.then((termTypesResponse: TermType[]) => {
				setTermTypes(termTypesResponse);
				if (termTypesResponse.length === 1) {
					setInitialValues({
						...initialValues,
						typeId: termTypesResponse[0].id,
						type: termTypesResponse[0].name
					});
					setUniqueOption(true);
				}
			})
			.catch(() => {
				showErrorToast(Constants.strings.errors.failedToGetTermTypes);
			})
			.finally(() => setIsLoading(false));
	}, []);

	const getActiveTerm = useCallback(() => {
		setIsLoading(true);
		termService
			.getById(activeTermId || "")
			.then((termResponse: Term) => {
				setActiveTerm(termResponse);
				setInitialValues({
					...initialValues,
					typeId: termResponse.type.id,
					type: termResponse.type.name
				});
			})
			.catch(() => {
				showErrorToast(Constants.strings.errors.failedToGetTerm);
			})
			.finally(() => setIsLoading(false));
	}, []);

	const getTerm = useCallback(() => {
		setIsLoading(true);
		termService
			.getById(termId || "")
			.then((termResponse: Term) => {
				setInitialValues({
					...initialValues,
					typeId: termResponse.type.id,
					type: termResponse.type.name,
					fileName: FileHelper.getFileNameFromUrl(termResponse.pdfUrl) || "",
					startDate: termResponse.startDate ?? ""
				});
			})
			.catch(() => {
				showErrorToast(Constants.strings.errors.failedToGetTerm);
			})
			.finally(() => setIsLoading(false));
	}, []);

	const cancelTerm = useCallback(() => {
		setIsLoading(true);
		termService
			.cancelTerm(termId || "")
			.then(() => {
				navigate(-1);
				showSuccessToast(Constants.strings.success.termCanceledTitle);
			})
			.catch(() => {
				showErrorToast(Constants.strings.errors.somethingWentWrong);
			})
			.finally(() => setIsLoading(false));
	}, []);

	useEffect(() => {
		setLoadSelect(!(activeTermId || termId));
		if (activeTermId) {
			getActiveTerm();
			setTitle(Constants.strings.pages.term.updateTerm);
			setIsEditForm(true);
		} else if (termId) {
			getTerm();
			setTitle(Constants.strings.pages.term.editTerm);
		} else {
			getTermTypes();
		}
	}, []);

	return isLoading ? (
		<div className="p-40">
			<Loading type="primary" size={75} />
		</div>
	) : (
		<>
			<BaseForm
				initialValues={initialValues}
				isEditForm={isEditForm}
				isSubmitting={isSubmitting}
				onSubmit={onSubmit}
				cancel={() => setShowModal(!showModal)}
				validationSchema={validationSchema}
				title={title}
			>
				{(formik) => {
					return (
						<>
							<p className="text-lg font-semibold pb-2">
								{activeTermId
									? activeTerm?.type.name
									: Constants.strings.pages.term.label}
							</p>
							{loadSelect &&
								(!uniqueOption ? (
									<Select
										name="typeId"
										label={Constants.strings.pages.term.termType.label}
										placeholder={
											Constants.strings.pages.term.termType.placeholder
										}
										options={termTypes.map((termType) => ({
											label: termType.name,
											value: termType.id
										}))}
										isClearable={false}
										value={formik.values.typeId}
										getOptionValue={(option) => option.value}
										setFieldValue={formik.setFieldValue}
										error={formik.errors.typeId as string}
										touched={!!formik.touched.typeId}
										className="pb-2"
										required
									/>
								) : (
									<Input
										type="text"
										name="type"
										uniqueOption={uniqueOption}
										label={Constants.strings.pages.term.termType.label}
										value={formik.values.type}
										error={formik.errors.type}
										touched={formik.touched.type}
										onBlur={formik.handleBlur}
										onChange={formik.handleChange}
										disabled
										className=""
										required
									/>
								))}
							<FileInput
								label="Anexar termo"
								name="fileName"
								acceptedFormats={["pdf"]}
								details="PDF"
								onAttachmentChange={(newDocument: string | null) =>
									formik.setFieldValue("fileName", newDocument)
								}
								value={formik.values.fileName}
								maxByteSize={MAX_DOCUMENT_SIZE}
								error={formik.errors.fileName}
								touched={formik.touched.fileName}
								className="pb-2"
								setFieldValue={formik.setFieldValue}
								termId={termId || ""}
							/>
							<SwitchInput
								name="activateNow"
								label="Ativar agora"
								onChange={(newValue) => {
									formik.setFieldValue("activateNow", newValue);
									if (newValue) {
										formik.setFieldValue(
											"startDate",
											DateTimeHelper.getCurrentDate()
										);
									} else {
										formik.setFieldValue("startDate", "");
									}
								}}
								value={formik.values.activateNow}
								className="pb-2"
								hideOptional
							/>
							<Input
								type="date"
								name="startDate"
								label="Data de publicação"
								placeholder="DD/MM/AAAA"
								min={DateTimeHelper.getCurrentDate()}
								max="2100-12-31"
								value={formik.values.startDate}
								error={formik.errors.startDate}
								touched={formik.touched.startDate}
								onBlur={formik.handleBlur}
								onChange={formik.handleChange}
								hideOptional
							/>
						</>
					);
				}}
			</BaseForm>
			<ConfirmationModal
				show={showModal}
				onCancel={() => setShowModal(!showModal)}
				onProceed={cancelTerm}
			/>
		</>
	);
}
